import React, { useCallback, useMemo } from 'react';

import {
  IonIcon,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { informationCircleOutline, timeOutline } from 'ionicons/icons';

import { Redirect, Route } from 'react-router-dom';

import Actions, { Action } from 'components/Actions';
import ErrorContent from 'components/ErrorContent';
import Modal, {
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalTitle,
} from 'components/Modal';
import Typography from 'components/Typography';
import { useIsIncluded } from 'hooks/useIsIncluded';
import { TAB_ACTIONS_LOG, TAB_DETAILS } from 'scenes/FinancePage/SA/constants';
import {
  ACTION_APPROVE,
  ACTION_MORE_INFO,
  ACTION_REJECT,
  ACTION_UNDO,
} from 'scenes/FinancePage/SA/constants';
import {
  useApprove,
  useMoreInfo,
  useReject,
  useUndo,
} from 'scenes/FinancePage/SA/data/mutations';
import { useIsUpdating } from 'scenes/FinancePage/SA/data/useIsUpdating';
import { useLoadSA } from 'scenes/FinancePage/SA/data/useLoadSA';

import ActionsLog from './components/ActionsLog';
import Details from './components/Details';
import DetailsSkeleton from './components/DetailsSkeleton';

const SADetailsModal = ({
  id,
  parentPath,
  onClose = () => {},
  onSubmit = () => {},
}: any) => {
  const { data, isRefetching, isLoading, isError, error, isSuccess } =
    useLoadSA({ id });
  const isUpdating = useIsUpdating();
  const { actionsLog, ...details } = data || {};

  const isActionAllowed = useIsIncluded(data?.actions);

  const [approve] = useApprove();
  const [reject] = useReject();
  const [undo] = useUndo();
  const [moreInfo] = useMoreInfo();

  const handleApprove = useCallback(() => {
    approve({ id });
  }, [id, approve]);

  const handleReject = useCallback(() => {
    reject({ id });
  }, [id, reject]);

  const handleUndo = useCallback(() => {
    undo({ id });
  }, [id, undo]);

  const handleMoreInfo = useCallback(() => {
    moreInfo({ id });
  }, [id, moreInfo]);

  const routes = useMemo(() => {
    return {
      [TAB_DETAILS]: `${parentPath}/${TAB_DETAILS}`,
      [TAB_ACTIONS_LOG]: `${parentPath}/${TAB_ACTIONS_LOG}`,
    };
  }, [parentPath]);

  const renderDetailsComponent = useMemo(() => {
    switch (true) {
      case isError: //Loaded with error response
        return <ErrorContent text={error} />;
      case isSuccess: //Loaded with normal response
        return <Details data={details} />;
      case isLoading:
        return <DetailsSkeleton />; //No loaded
      default:
        return null;
    }
  }, [isLoading, isRefetching, isSuccess, isError, details, error]);

  return (
    <Modal onClose={onClose} onSubmit={onSubmit}>
      <span></span>
      <IonReactRouter>
        <IonTabs>
          <IonTabBar slot={'top'}>
            <IonTabButton tab={TAB_DETAILS} href={routes[TAB_DETAILS]}>
              <Typography>Details</Typography>
              <IonIcon icon={informationCircleOutline} />
            </IonTabButton>
            <IonTabButton tab={TAB_ACTIONS_LOG} href={routes[TAB_ACTIONS_LOG]}>
              <Typography>Action Logs</Typography>
              <IonIcon icon={timeOutline} />
            </IonTabButton>
          </IonTabBar>
          <IonRouterOutlet>
            <Redirect exact path={parentPath} to={routes[TAB_DETAILS]} />
            <Route exact path={routes[TAB_DETAILS]}>
              <ModalContent isRefetching={isRefetching} isUpdating={isUpdating}>
                {renderDetailsComponent}
              </ModalContent>
            </Route>
            <Route exact path={routes[TAB_ACTIONS_LOG]}>
              <ModalContent isRefetching={isRefetching} isUpdating={isUpdating}>
                <ActionsLog data={actionsLog} />
              </ModalContent>
            </Route>
          </IonRouterOutlet>
        </IonTabs>
      </IonReactRouter>
      <ModalFooter>
        <ModalCloseButton />
        <ModalTitle>SA #{id}</ModalTitle>
        <Actions header={'More actions'} limit={1}>
          {isActionAllowed(ACTION_APPROVE) ? (
            <Action
              onClick={handleApprove}
              color={'primary'}
              label={'Approve'}
            />
          ) : null}
          {isActionAllowed(ACTION_MORE_INFO) ? (
            <Action onClick={handleMoreInfo} label={'Request more info'} />
          ) : null}
          {isActionAllowed(ACTION_UNDO) ? (
            <Action onClick={handleUndo} label={'Undo'} />
          ) : null}
          {isActionAllowed(ACTION_REJECT) ? (
            <Action onClick={handleReject} label={'Reject'} />
          ) : null}
        </Actions>
      </ModalFooter>
    </Modal>
  );
};

export default SADetailsModal;
